import { useCallback, useMemo } from 'react';
import { useMytaverse } from '@providers/MytaverseProvider';
import { getHRData } from '@features/avaturn/helpers/hr';
import { IXRHrGenderTypes } from '@clients/ek/interfaces';
import { AvaturnStatuses, GenderType } from '@features/avaturn/interfaces';

import {
  IXRHrBrandTypes,
  IXRHrCredStatusTypes,
  IXRHrPersonaTypes,
  ChangeAvatarTypes,
} from '@clients/ek/interfaces';
import { useAvaturnHelperType } from '@features/avaturn/pages/Avaturn/interfaces';
import ParticipantsService from '@services/ParticipantsService';
import { AvatarTypes } from '@interfaces/avatar';
import AvaturnService from '@features/avaturn/services/AvaturnService';
import { MytaverseLogger } from '@helpers/logger';
import { getCatchErrorMessage } from '@helpers/error';
import { EventRole } from '@interfaces/event';

const getAvaturnBrand = (credentialStatus: string, brand: string): string => {
  let brandWithCreds = brand;

  if (credentialStatus.toUpperCase() === IXRHrCredStatusTypes.Pre) {
    brandWithCreds = 'ek-no-brand';
  }

  return brandWithCreds;
};

export const getAvaturnPersona = (props: {
  brand: string;
  persona: string;
  reason?: string;
}) => {
  const { brand, persona, reason } = props;

  if (brand === IXRHrBrandTypes.SD) {
    const newPersona =
      persona.toUpperCase() === IXRHrPersonaTypes.Purser
        ? IXRHrPersonaTypes.Purser
        : IXRHrPersonaTypes.CabinCrew;

    switch (reason) {
      case ChangeAvatarTypes.UniformHalf: {
        return `${newPersona}|${reason}`;
      }
      case ChangeAvatarTypes.UniformFull: {
        return newPersona;
      }
      case ChangeAvatarTypes.UniformFullSubsequent: {
        return `${newPersona}|${reason}`;
      }
    }

    if (persona.toUpperCase() === IXRHrPersonaTypes.Abinitio) {
      return IXRHrPersonaTypes.Abinitio;
    }

    return persona;
  }

  return undefined;
};

const useAvaturnHelper: useAvaturnHelperType = ({
  currentEventParticipant,
} = {}) => {
  const { user, configs } = useMytaverse();

  const hrData = useMemo(() => {
    return getHRData(user?.hrData);
  }, [user]);

  const hasUniformed = useMemo(() => {
    return !!user?.avatars.find(({ tags = [] }) => tags.includes('UNIFORM'));
  }, [user?.avatars]);

  const getProcessedBrand = useCallback(() => {
    return getAvaturnBrand(hrData.ixrCredentialStatus, hrData.brand);
  }, [hrData]);

  const getGender = useCallback(() => {
    if (!hrData?.gender) {
      return;
    }
    return hrData?.gender?.toUpperCase() === IXRHrGenderTypes.F
      ? GenderType.FEMALE
      : GenderType.MALE;
  }, [hrData?.gender]);

  const getToken = useCallback(() => {
    const { ixrCredentialStatus, brand } = hrData;

    if (!brand || !ixrCredentialStatus) {
      console.log('Avaturn: getToken Missing required parameters');
      return null;
    }

    if (
      configs?.avaturn?.useSelfHosted &&
      configs?.avaturn?.tokens?.selfHosted
    ) {
      return configs?.avaturn?.tokens?.selfHosted;
    }

    if (ixrCredentialStatus.toUpperCase() === IXRHrCredStatusTypes.Pre) {
      return configs?.avaturn?.tokens?.ek;
    } else {
      switch (brand.toUpperCase()) {
        case IXRHrBrandTypes.Dnata:
          return configs?.avaturn?.tokens?.dnata;

        case IXRHrBrandTypes.Marhaba:
          return configs?.avaturn?.tokens?.marhaba;

        case IXRHrBrandTypes.Ekas:
          return configs?.avaturn?.tokens?.ekas;
      }
    }

    return configs?.avaturn?.tokens?.ek;
  }, [hrData, configs]);

  const getAssetParams = useCallback(
    (withPersona = false, reason: string = ChangeAvatarTypes.UniformHalf) => {
      const { gender, brand, persona } = hrData;

      if (!withPersona && currentEventParticipant?.isDemo) {
        return {
          credentialStatus: IXRHrCredStatusTypes.FT,
          gender,
          brand: IXRHrBrandTypes.Demo,
        };
      }

      if (
        brand === IXRHrBrandTypes.SD &&
        !withPersona &&
        currentEventParticipant?.role &&
        [EventRole.Admin, EventRole.Coordinator].includes(
          currentEventParticipant?.role,
        )
      ) {
        return {
          credentialStatus: IXRHrCredStatusTypes.FT,
          gender,
          brand: IXRHrBrandTypes.SD_FACILITATOR,
        };
      }

      let finalReason = reason;
      if (
        withPersona &&
        reason === ChangeAvatarTypes.UniformHalf &&
        hasUniformed
      ) {
        finalReason = ChangeAvatarTypes.UniformFullSubsequent;
      }

      return {
        credentialStatus: hrData.ixrCredentialStatus,
        gender,
        brand,
        persona: getAvaturnPersona({
          brand,
          persona,
          reason: withPersona && finalReason ? finalReason : undefined,
        }),
      };
    },
    [
      hrData,
      hasUniformed,
      currentEventParticipant?.isDemo,
      currentEventParticipant?.role,
    ],
  );

  const getChangeAvatarParams = useCallback(
    (reason?: string) => {
      const params = {
        ixrCredentialStatus: user?.hrData?.ixrCredentialStatus.toUpperCase(),
        gender: user?.hrData?.gender.toUpperCase(),
        brand: user?.hrData?.brand.toUpperCase(),
        persona: user?.hrData?.persona.toUpperCase(),
        tags: ['UNIFORM'],
      };

      if (reason === ChangeAvatarTypes.UniformTryOn) {
        //Abinitio will try on Cabin_Crew attire
        params.persona =
          user?.hrData?.persona?.toUpperCase() === IXRHrPersonaTypes.Purser
            ? IXRHrPersonaTypes.Purser
            : IXRHrPersonaTypes.CabinCrew;

        params.persona += `|${ChangeAvatarTypes.UniformTryOn}`;
      }

      const brand = getAvaturnBrand(hrData.ixrCredentialStatus, hrData.brand);

      return { ...params, brand };
    },
    [hrData],
  );

  const deleteAvatar = useCallback(async (avatarId: string, brand: string) => {
    const avatars = await ParticipantsService.getParticipantAvaturnAvatars();

    const avatar = avatars.find((item) => item.avatarId === avatarId);

    let listOfAvatars =
      await ParticipantsService.deleteParticipantAvatarFromList({
        avatarType: AvatarTypes.AVATURN,
        avatarId,
        brand,
        hardDelete: true,
      });

    if (avatar?.changeAssets?.childAvaturnId) {
      try {
        const childAvatar = await AvaturnService.getAvatarInfo(
          avatar.changeAssets.childAvaturnId,
        );

        const isAvatarAttached = avatars.find(
          (item) => item.avatarId === childAvatar.avatarId,
        );

        if (!isAvatarAttached) {
          await ParticipantsService.deleteParticipantAvatarFromList({
            avatarType: AvatarTypes.AVATURN,
            avatarId: childAvatar.avatarId,
            brand,
            hardDelete: true,
          });
        }
      } catch (error) {
        MytaverseLogger.error(
          `childAvaturn deletion: ${getCatchErrorMessage(error)}`,
        );
      }
    }

    const limitedAvatars = avatars.filter(
      (avatar) =>
        avatar?.status === AvaturnStatuses.LIMITED &&
        avatar?.changeAssets?.baseAvatarId === avatarId &&
        avatar.tags?.includes('UNIFORM'),
    );

    if (limitedAvatars.length > 0) {
      for (const avatar of limitedAvatars) {
        listOfAvatars =
          await ParticipantsService.deleteParticipantAvatarFromList({
            avatarType: AvatarTypes.AVATURN,
            avatarId: avatar.avatarId,
            brand,
            hardDelete: true,
          });
      }
    }

    return listOfAvatars;
  }, []);

  const exportPreprocess = useCallback(
    async (avaturnId: string, avatarId = '') => {
      if (!avatarId) {
        try {
          return await AvaturnService.deleteAvaturn(avaturnId);
        } catch (e) {
          MytaverseLogger.log(
            `deleteAvaturn error: ${getCatchErrorMessage(e)}`,
          );
        }
      }

      const param = getChangeAvatarParams(ChangeAvatarTypes.UniformTryOn);

      if (param.brand !== IXRHrBrandTypes.SD) {
        try {
          return await AvaturnService.deleteAvaturn(avaturnId);
        } catch (e) {
          MytaverseLogger.log(
            `deleteAvaturn error: ${getCatchErrorMessage(e)}`,
          );
        }
      }

      try {
        const result = await AvaturnService.cloneAvatar({
          ...param,
          avatarId,
          tags: [ChangeAvatarTypes.UniformTryOn],
          renderPic: false,
        });
        MytaverseLogger.log(`cloneAvatar: ${JSON.stringify(result)}`);
      } catch (e) {
        MytaverseLogger.log(`cloneAvatar error: ${getCatchErrorMessage(e)}`);
      }
    },
    [getChangeAvatarParams],
  );

  return {
    getProcessedBrand,
    getGender,
    getToken,
    getAssetParams,
    getChangeAvatarParams,
    deleteAvatar,
    exportPreprocess,
  };
};

export default useAvaturnHelper;
